// src/modules/shared/hooks/modal.ts

import { useEffect, useRef } from 'react';
import { useCloseRequest } from './use-close-request';
import type { UseModalProps } from '../types/modals/use-modal.type';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** Returns controls that can receive focus, including inherited disabled state. */
const focusableElements = (dialog: HTMLElement): HTMLElement[] =>
  Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.matches(':disabled')
  );
const modalStack: symbol[] = [];
let bodyOverflowBeforeFirstModal: string | null = null;

const registerModal = (modalId: symbol) => {
  if (modalStack.length === 0) {
    bodyOverflowBeforeFirstModal = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  modalStack.push(modalId);
};

const unregisterModal = (modalId: symbol): boolean => {
  const modalIndex = modalStack.lastIndexOf(modalId);
  const wasTopmost = modalIndex >= 0 && modalIndex === modalStack.length - 1;

  if (modalIndex >= 0) {
    modalStack.splice(modalIndex, 1);
  }

  if (modalStack.length === 0) {
    document.body.style.overflow = bodyOverflowBeforeFirstModal ?? '';
    bodyOverflowBeforeFirstModal = null;
  }

  return wasTopmost;
};

const isTopmostModal = (modalId: symbol): boolean =>
  modalStack[modalStack.length - 1] === modalId;

/**
 * Manages a modal's topmost Escape/focus ownership and the shared body lock.
 *
 * @param props - Visibility, Close policy, and focus-trap container.
 * @returns The shared close-layer identity for nested surfaces.
 */
export const useModal = ({
  isOpen,
  onClose,
  dialogRef,
  dismissible = true,
  returnFocusRef,
}: UseModalProps) => {
  const modalIdRef = useRef(Symbol('modal'));
  const closeLayerId = useCloseRequest({
    isOpen,
    onClose,
    dismissible,
    modal: true,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const modalId = modalIdRef.current;
    const requestedFocusTarget = returnFocusRef?.current;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    registerModal(modalId);

    const animationFrame = window.requestAnimationFrame(() => {
      if (!isTopmostModal(modalId)) {
        return;
      }

      const firstFocusable = dialogRef.current
        ? focusableElements(dialogRef.current)[0]
        : undefined;

      (firstFocusable ?? dialogRef.current)?.focus();
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopmostModal(modalId)) {
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }

      const focusable = focusableElements(dialogRef.current);

      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      const activeIsFocusable = focusable.some(
        (element) => element === document.activeElement
      );
      if (!activeIsFocusable) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('keydown', handleKeyDown);

      const shouldRestoreFocus = unregisterModal(modalId);
      const focusTarget = requestedFocusTarget ?? previouslyFocused;
      if (shouldRestoreFocus && focusTarget?.isConnected) {
        focusTarget.focus();
      }
    };
  }, [dialogRef, isOpen, returnFocusRef]);

  return closeLayerId;
};

import { useCallback } from 'react';
import { useAlert } from 'src/contexts/alert-context';

/**
 * Custom hook to copy text to clipboard and show a notification.
 * @returns A function to copy text to clipboard.
 */
export const useCopyToClipboard = () => {
  const { showAlert } = useAlert();

  return useCallback(
    async (text: string) => {
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
        // TODO: check when implementing i18n
        showAlert('Texto copiado al portapapeles.', 'success');
      } catch (_err) {
        // TODO: check when implementing i18n
        showAlert('Error al copiar el texto.', 'error');
      }
    },
    [showAlert]
  );
};
